import React, { useEffect } from "react";
import "./App.css";
import "./public-brand.css";
import "./typography.css";
import "./contact-links.css";
import "./homepage-spacing.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import { trackPageView, trackEvent } from "./lib/analytics";
import useReveal from "./hooks/useReveal";
import useSeo from "./hooks/useSeo";
import HOME_SEO from "./homeSeo.json";
import Header from "./components/Header";
import Hero from "./components/Hero";
import SeoIntro from "./components/SeoIntro";
import Benefits from "./components/Benefits";
import SellSection from "./components/SellSection";
import BuySection from "./components/BuySection";
import Gallery from "./components/Gallery";
import FeaturedDeals from "./components/FeaturedDeals";
import Estimator from "./components/Estimator";
import NewProducts from "./components/NewProducts";
import Buyback from "./components/Buyback";
import Brands from "./components/Brands";
import About from "./components/About";
import Contact from "./components/Contact";
import Footer from "./components/Footer";
import FloatingWhatsApp from "./components/FloatingWhatsApp";
import MobileCTABar from "./components/MobileCTABar";
import Admin from "./components/Admin";
import WorkerPhotos from "./components/WorkerPhotos";
import StockAdd from "./components/StockAdd";
import AdminUpload from "./components/AdminUpload";
import CategoryPage from "./components/CategoryPage";
import StorePage from "./components/StorePage";
import PosterPage from "./components/PosterPage";
import SeoPage from "./components/SeoPage";
import ProductDetail from "./components/ProductDetail";
import EnquiryListPage from "./components/EnquiryListPage";
import EnquiryBar from "./components/EnquiryBar";
import { EnquiryProvider } from "./context/EnquiryContext";
import NotFound from "./components/NotFound";
import { CorpProcess } from "./components/corporate/CorpProcess";
import { CorporateBuybackBanner } from "./components/corporate/CorporateBuybackBanner";
import { CorpProjects } from "./components/corporate/CorpProjects";
import { CorpTrust } from "./components/corporate/CorpTrust";

function HomePage() {
  useReveal();
  useSeo(HOME_SEO);
  return (
    <div className="App public-site">
      <Header />
      <main className="home-layout" data-testid="homepage-content">
        <Hero />
        <SeoIntro />
        <Benefits />
        <FeaturedDeals />
        <SellSection />
        <BuySection />
        <Gallery />
        <Estimator />
        <NewProducts />
        <Buyback />
        <CorpProcess />
        <CorporateBuybackBanner />
        <CorpProjects />
        <CorpTrust />
        <Brands />
        <About />
        <Contact />
      </main>
      <Footer />
      <FloatingWhatsApp />
    </div>
  );
}

function GlobalMobileCTA() {
  const { pathname } = useLocation();
  if (/^\/(admin|warehouse|godown|poster)(\/|$)/.test(pathname) || pathname === "/stock/add") return null;
  return <MobileCTABar key={pathname} />;
}

function AnalyticsTracker() {
  const location = useLocation();

  useEffect(() => {
    trackPageView(location.pathname + location.search);
  }, [location.pathname, location.search]);

  useEffect(() => {
    const onClick = (e) => {
      const a = e.target.closest && e.target.closest("a[href]");
      if (!a || a.dataset.kpTracked === "1") return;
      const href = a.getAttribute("href") || "";
      const testid = a.getAttribute("data-testid") || undefined;
      const isWhatsApp = /wa\.me|api\.whatsapp\.com|whatsapp/i.test(href);
      const quoteIds = ["hero-whatsapp-btn", "hero-quote-whatsapp", "estimator-quote", "new-products-quote", "mobile-cta-quote", "corporate-banner-value-hotspot", "corporate-banner-valuation-hotspot"];
      const isQuote = quoteIds.includes(testid);
      let eventName;
      let contactMethod;
      if (/^tel:/i.test(href)) {
        eventName = "call_click";
        contactMethod = "phone";
      } else if (/^mailto:/i.test(href)) {
        eventName = "email_click";
        contactMethod = "email";
      } else if (isQuote) {
        eventName = "get_quote_click";
        contactMethod = isWhatsApp ? "whatsapp" : "form";
      } else if (isWhatsApp) {
        eventName = "whatsapp_click";
        contactMethod = "whatsapp";
      } else {
        return;
      }
      a.dataset.kpTracked = "1";
      setTimeout(() => { delete a.dataset.kpTracked; }, 800);
      const params = { page_path: window.location.pathname, testid, contact_method: contactMethod };
      // Enquiry-level product context (code/category) on the SAME event — no extra tag, no extra lead count.
      const pCode = a.dataset.productCode;
      const pCat = a.dataset.productCategory;
      const iCodes = a.dataset.itemCodes;
      const iCount = a.dataset.itemCount;
      if (pCode) params.item_code = pCode;
      if (pCat) params.item_category = pCat;
      if (iCodes) params.item_codes = iCodes.slice(0, 90);
      if (iCount) params.item_count = Number(iCount) || undefined;
      // WhatsApp query strings can contain a visitor's enquiry and contact details.
      if (isWhatsApp) {
        const url = new URL(href, window.location.origin);
        params.link_url = url.origin + url.pathname;
      }
      trackEvent(eventName, params);
    };
    document.addEventListener("click", onClick, true);
    return () => document.removeEventListener("click", onClick, true);
  }, []);

  return null;
}

function App() {
  return (
    <BrowserRouter>
      <EnquiryProvider>
        <AnalyticsTracker />
        <Routes>
          <Route path="/" element={<HomePage />} />
          <Route path="/store" element={<StorePage />} />
          <Route path="/enquiry" element={<EnquiryListPage />} />
          <Route path="/warehouse" element={<StockAdd />} />
          <Route path="/godown" element={<WorkerPhotos />} />
          <Route path="/godown-photos" element={<Navigate to="/godown" replace />} />
          <Route path="/stock/add" element={<Navigate to="/warehouse" replace />} />
          <Route path="/bengaluru" element={<Navigate to="/bangalore" replace />} />
          <Route path="/stock/:category" element={<CategoryPage />} />
          <Route path="/product/:id" element={<ProductDetail />} />
          <Route path="/:slug" element={<SeoPage />} />
          <Route path="/poster" element={<PosterPage />} />
          <Route path="/poster/:category" element={<PosterPage />} />
          <Route path="/admin" element={<Admin />} />
          <Route path="/admin/enquiries" element={<Admin />} />
          <Route path="/admin/upload" element={<AdminUpload />} />
          <Route path="*" element={<NotFound />} />
        </Routes>
        <GlobalMobileCTA />
        <EnquiryBar />
        <Toaster position="top-center" richColors closeButton />
      </EnquiryProvider>
    </BrowserRouter>
  );
}

export default App;
